import { Star } from 'lucide-react';
import LightningIcon from './icons/LightningIcon';
import ClockIcon from './icons/ClockIcon';
import ChapterGridTooltip from './ChapterGridTooltip';
import { formatTotalTime, getCourseTitle } from './courseUtils';

const QuizCard = ({ course, index, language, showStats, activeQuizSession, isFavorite, onToggleFavorite, onStart, onPurchase }) => {
    const progress = course.progress || 0;
    const comprehension = course.comprehension;
    const quizLocked = course.requiresUpgrade;

    return (
        <div
            key={course.idCourse}
            {...(index === 0 ? { 'data-help': 'courses-card-first' } : {})}
            className={`bg-white dark:bg-[#121212] rounded-2xl p-4 lg:p-6 border border-gray-200 dark:border-white/10 transition-all shadow-sm dark:shadow-none flex flex-col lg:flex-row lg:items-center justify-between gap-4 lg:gap-6 h-full ${
                quizLocked
                    ? 'opacity-60 grayscale pointer-events-none'
                    : 'hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'
            }`}
        >
            {/* Left: Title + Metrics */}
            <div className="flex-1 min-w-0">
                {/* Title */}
                <h4 className="text-base lg:text-lg font-bold text-gray-900 dark:text-white mb-3 lg:mb-4 tracking-tight flex items-center gap-3 flex-wrap">
                    {onToggleFavorite && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onToggleFavorite(course.idCourse); }}
                            className="flex-shrink-0 p-0.5 transition-colors"
                            title={isFavorite ? (language === 'fr' ? 'Retirer des favoris' : 'Remove from favorites') : (language === 'fr' ? 'Ajouter aux favoris' : 'Add to favorites')}
                        >
                            <Star size={16} className={isFavorite ? 'text-amber-400 fill-amber-400' : 'text-gray-300 dark:text-gray-600 hover:text-amber-300 dark:hover:text-amber-500'} />
                        </button>
                    )}
                    {quizLocked && (
                        <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    )}
                    {getCourseTitle(course, language)}
                    <span className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider bg-purple-500 text-white shadow-lg shadow-purple-500/30">
                        Quiz
                    </span>
                    {quizLocked && (
                        <span className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400">
                            Premium
                        </span>
                    )}
                </h4>

                {/* Stats rows - conditionally rendered */}
                {showStats && (
                <>
                {/* Row 1: Progression | Compréhension | Réussite */}
                <div className="grid grid-cols-2 lg:flex lg:flex-row gap-4 lg:gap-8 mb-3">
                    {(() => {
                        const statuses = course.chapterStatuses || [];
                        const totalQ = statuses.length;
                        const hasSession = course.hasActiveSession && totalQ > 0;
                        let sessionPct = 0;
                        let historicPct = 0;
                        let progressVal = progress;

                        if (hasSession) {
                            const sessionCount = statuses.filter(ch => ch.sv).length;
                            const historicOnlyCount = statuses.filter(ch => ch.v && !ch.sv).length;
                            sessionPct = Math.round((sessionCount / totalQ) * 100);
                            historicPct = Math.round((historicOnlyCount / totalQ) * 100);
                            progressVal = sessionPct;
                        }

                        return (
                            <ChapterGridTooltip
                                chapterStatuses={statuses}
                                language={language}
                                isQuiz={true}
                                hasActiveSession={hasSession}
                                className="flex-1 min-w-0"
                            >
                                <div className="flex flex-col gap-1.5">
                                    <div className="flex justify-between items-center w-full gap-2">
                                        <span className="text-[11px] font-black uppercase tracking-widest text-blue-500 dark:text-blue-400">Progression</span>
                                        <span className="text-xs font-black text-gray-900 dark:text-white">{progressVal}%</span>
                                    </div>
                                    <div className="h-1.5 w-full bg-gray-200 dark:bg-white/5 rounded-full overflow-hidden relative">
                                        {hasSession && historicPct > 0 && (
                                            <div className="absolute inset-y-0 left-0 bg-sky-300/60" style={{ width: `${sessionPct + historicPct}%` }}></div>
                                        )}
                                        <div className="h-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)] relative" style={{ width: `${hasSession ? sessionPct : progress}%` }}></div>
                                    </div>
                                </div>
                            </ChapterGridTooltip>
                        );
                    })()}

                    <ChapterGridTooltip
                        chapterStatuses={course.chapterStatuses || []}
                        language={language}
                        isQuiz={true}
                        mode="comprehension"
                        className="flex-1 min-w-0"
                    >
                        <div className="flex flex-col gap-1.5">
                            <div className="flex justify-between items-center w-full gap-2">
                                <span className="text-[11px] font-black uppercase tracking-widest text-purple-500 dark:text-purple-400">
                                    {language === 'fr' ? 'Compréhension' : 'Comprehension'}
                                </span>
                                <span className="text-xs font-black text-gray-900 dark:text-white">
                                    {comprehension != null ? `${comprehension}%` : '-'}
                                </span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-200 dark:bg-white/5 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all ${comprehension != null
                                        ? 'bg-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.5)]'
                                        : 'bg-transparent'}`}
                                    style={{ width: `${comprehension || 0}%` }}
                                ></div>
                            </div>
                        </div>
                    </ChapterGridTooltip>

                    <ChapterGridTooltip
                        chapterStatuses={[]}
                        language={language}
                        customContent={course.totalAnswersGiven > 0 ? (
                            <>
                                <p className="text-[10px] text-gray-300 mb-2 leading-relaxed">
                                    {language === 'fr'
                                        ? 'Nombre total de réponses correctes sur l\'ensemble de tes tentatives de quiz.'
                                        : 'Total correct answers across all your quiz attempts.'}
                                </p>
                                <div className="flex items-baseline gap-2 mb-1">
                                    <span className="text-lg font-black text-white">{course.totalCorrectAnswers}</span>
                                    <span className="text-[10px] text-gray-400">/</span>
                                    <span className="text-lg font-black text-white">{course.totalAnswersGiven}</span>
                                    <span className="text-[10px] text-gray-400">{language === 'fr' ? 'réponses' : 'answers'}</span>
                                </div>
                                <div className="h-1.5 w-full bg-gray-700 rounded-full overflow-hidden mb-1">
                                    <div className="h-full bg-rose-500 rounded-full" style={{ width: `${course.successRate || 0}%` }}></div>
                                </div>
                                <p className="text-[10px] font-bold text-rose-400">{course.successRate}% {language === 'fr' ? 'de réussite' : 'success rate'}</p>
                            </>
                        ) : null}
                        className="flex-1 min-w-0"
                    >
                        <div className="flex flex-col gap-1.5">
                            <div className="flex justify-between items-center w-full gap-2">
                                <span className="text-[11px] font-black uppercase tracking-widest text-rose-500 dark:text-rose-400">
                                    {language === 'fr' ? 'Réussite' : 'Success'}
                                </span>
                                <span className="text-xs font-black text-gray-900 dark:text-white">
                                    {course.totalAnswersGiven > 0
                                        ? `${course.totalCorrectAnswers}/${course.totalAnswersGiven}`
                                        : '-'}
                                </span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-200 dark:bg-white/5 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all ${course.successRate != null
                                        ? 'bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.5)]'
                                        : 'bg-transparent'}`}
                                    style={{ width: `${course.successRate || 0}%` }}
                                ></div>
                            </div>
                        </div>
                    </ChapterGridTooltip>
                </div>

                {/* Row 2: Dernier Score | Meilleur Score | Temps */}
                <div className="grid grid-cols-2 lg:flex lg:flex-row gap-4 lg:gap-8">
                    <ChapterGridTooltip
                        chapterStatuses={course.chapterStatuses || []}
                        language={language}
                        isQuiz={true}
                        mode="score"
                        scoreAnswers={course.lastSessionAnswers}
                        correctAnswers={course.correctAnswers}
                        scoreDescription={language === 'fr' ? 'Résultat par question de ta dernière session.' : 'Per-question result from your last session.'}
                        className="flex-1 min-w-0"
                    >
                        <div className="flex flex-col gap-1.5">
                            <div className="flex justify-between items-center w-full gap-2">
                                <span className="text-[11px] font-black uppercase tracking-widest text-amber-500 dark:text-amber-400">
                                    {language === 'fr' ? 'Dernier Score' : 'Last Score'}
                                </span>
                                <span className="text-xs font-black text-gray-900 dark:text-white">
                                    {course.lastScore != null ? `${course.lastScore}%` : '-'}
                                </span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-200 dark:bg-white/5 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all ${course.lastScore != null
                                        ? 'bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.5)]'
                                        : 'bg-transparent'}`}
                                    style={{ width: `${course.lastScore || 0}%` }}
                                ></div>
                            </div>
                        </div>
                    </ChapterGridTooltip>

                    <ChapterGridTooltip
                        chapterStatuses={course.chapterStatuses || []}
                        language={language}
                        isQuiz={true}
                        mode="score"
                        scoreAnswers={course.bestSessionAnswers}
                        correctAnswers={course.correctAnswers}
                        scoreDescription={language === 'fr' ? 'Résultat par question de ta meilleure session.' : 'Per-question result from your best session.'}
                        className="flex-1 min-w-0"
                    >
                        <div className="flex flex-col gap-1.5">
                            <div className="flex justify-between items-center w-full gap-2">
                                <span className="text-[11px] font-black uppercase tracking-widest text-emerald-500 dark:text-emerald-400">
                                    {language === 'fr' ? 'Meilleur Score' : 'Best Score'}
                                </span>
                                <span className="text-xs font-black text-gray-900 dark:text-white">
                                    {course.bestScore != null ? `${course.bestScore}%` : '-'}
                                </span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-200 dark:bg-white/5 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all ${course.bestScore != null
                                        ? 'bg-emerald-500 shadow-[0_0_10px_rgba(52,211,153,0.5)]'
                                        : 'bg-transparent'}`}
                                    style={{ width: `${course.bestScore || 0}%` }}
                                ></div>
                            </div>
                        </div>
                    </ChapterGridTooltip>

                    <div className="flex flex-col gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-600">
                            {language === 'fr' ? 'Temps' : 'Time'}
                        </span>
                        <span className="text-xs font-black text-gray-900 dark:text-white mt-auto">
                            {formatTotalTime(course.totalTime) || '0min'}
                        </span>
                    </div>
                </div>
                </>
                )}
            </div>

            {/* Right: Action Button */}
            <div className="flex justify-center lg:justify-end shrink-0">
                {quizLocked ? (
                    <button
                        onClick={onPurchase}
                        className="pointer-events-auto px-6 py-2.5 rounded-xl font-black text-xs shadow-xl transition hover:scale-105 active:scale-95 flex items-center justify-center gap-2 whitespace-nowrap text-amber-700 dark:text-amber-300 bg-gradient-to-r from-amber-100 to-amber-200 dark:from-amber-500/20 dark:to-amber-600/20 shadow-amber-500/20 border border-amber-300 dark:border-amber-500/30"
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        {language === 'fr' ? 'Débloquer' : 'Unlock'}
                    </button>
                ) : (
                    <button
                        onClick={() => onStart(course)}
                        className="px-6 py-2.5 rounded-xl font-black text-xs text-white shadow-xl transition bg-gradient-to-r from-blue-600 to-blue-700 hover:scale-105 active:scale-95 shadow-blue-500/20 flex items-center gap-2 justify-center whitespace-nowrap"
                    >
                        {activeQuizSession && (
                            activeQuizSession.mode === 'flash'
                                ? <LightningIcon className="w-4 h-4" />
                                : <ClockIcon className="w-4 h-4" />
                        )}
                        {activeQuizSession
                            ? (language === 'fr' ? 'Continuer' : 'Continue')
                            : (course.progress > 0 || course.lastScore != null
                                ? (language === 'fr' ? 'Recommencer' : 'Restart')
                                : (language === 'fr' ? 'Commencer' : 'Start'))}
                    </button>
                )}
            </div>
        </div>
    );
};

export default QuizCard;
